/**
 * IndicatorGrid - Scalable CSS Grid Container
 *
 * MT5-style responsive grid layout for N indicators.
 * Auto-fills columns based on screen width.
 *
 * Layout:
 * - Desktop (xl): 4 columns
 * - Desktop (lg): 3 columns
 * - Tablet (md): 2 columns
 * - Mobile (xs/sm): 1 column
 *
 * @version 1.0.0
 * @created 2025-12-21
 */

import React, { memo, useState } from 'react';
import { Box, Typography, Menu, MenuItem, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * IndicatorGrid Component
 *
 * @param {React.ReactNode} children - IndicatorCard components
 * @param {string} symbol - Current symbol (e.g., "XAUUSD")
 * @param {array} availableSymbols - List of available symbols
 * @param {function} onSymbolChange - Callback when symbol changes
 * @param {number} columns - Override column count (optional)
 * @param {string} title - Grid title (optional)
 */
/**
 * Symbols with full QuantX historical data (100+ H4 bars for EMA calculations)
 */
const SYMBOLS_WITH_QUANTX_DATA = ['XAUUSD_ct'];

const IndicatorGrid = memo(({
    children,
    symbol,
    availableSymbols = [],
    onSymbolChange,
    columns,
    title = 'QuantX Analytics'
}) => {
    const [symbolMenuAnchor, setSymbolMenuAnchor] = useState(null);

    const handleSymbolClick = (event) => {
        if (onSymbolChange && availableSymbols.length > 0) {
            setSymbolMenuAnchor(event.currentTarget);
        }
    };

    const handleSymbolSelect = (newSymbol) => {
        if (onSymbolChange) {
            onSymbolChange(newSymbol);
        }
        setSymbolMenuAnchor(null);
    };

    const handleSymbolClose = () => {
        setSymbolMenuAnchor(null);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                backgroundColor: '#0A0A0F'
            }}
        >
            {/* Header Bar - Symbol Selector + Title */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    backgroundColor: '#0F0F12',
                    flexShrink: 0
                }}
            >
                {/* Left: Title */}
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#E4E4E7',
                        letterSpacing: '0.5px',
                        fontFamily: "'Inter', -apple-system, sans-serif"
                    }}
                >
                    {title}
                </Typography>

                {/* Right: Symbol Selector */}
                {availableSymbols.length > 0 && onSymbolChange && (
                    <>
                        <Box
                            onClick={handleSymbolClick}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    borderColor: 'rgba(255, 255, 255, 0.15)'
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#E4E4E7',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {symbol?.replace('_ct', '') || 'SELECT'}
                            </Typography>
                            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#71717A' }} />
                        </Box>

                        {/* Symbol Dropdown Menu */}
                        <Menu
                            anchorEl={symbolMenuAnchor}
                            open={Boolean(symbolMenuAnchor)}
                            onClose={handleSymbolClose}
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: '#1A1A1F',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        minWidth: '160px',
                                        mt: 0.5
                                    }
                                }
                            }}
                        >
                            {availableSymbols.map((sym) => {
                                const hasFullData = SYMBOLS_WITH_QUANTX_DATA.includes(sym);
                                return (
                                    <MenuItem
                                        key={sym}
                                        onClick={() => handleSymbolSelect(sym)}
                                        selected={sym === symbol}
                                        sx={{
                                            fontSize: '12px',
                                            fontFamily: 'monospace',
                                            color: sym === symbol ? '#27B56D' : '#E4E4E7',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '8px 12px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(39, 181, 109, 0.1)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(39, 181, 109, 0.15)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(39, 181, 109, 0.2)'
                                                }
                                            }
                                        }}
                                    >
                                        <span>{sym.replace('_ct', '')}</span>
                                        {hasFullData ? (
                                            <span style={{ fontSize: '9px', color: '#22c55e', fontWeight: 600 }}>READY</span>
                                        ) : (
                                            <span style={{ fontSize: '9px', color: '#fbbf24', fontWeight: 600 }}>LOADING</span>
                                        )}
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </>
                )}
            </Box>

            {/* Scrollable Grid Container */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '12px',
                    // Custom scrollbar
                    '&::-webkit-scrollbar': {
                        width: '6px'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.15)'
                        }
                    }
                }}
            >
                {/* CSS Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: columns
                            ? `repeat(${columns}, 1fr)`
                            : {
                                xs: '1fr',                           // 1 column
                                sm: '1fr',                           // 1 column
                                md: 'repeat(2, 1fr)',                // 2 columns
                                lg: 'repeat(3, 1fr)',                // 3 columns
                                xl: 'repeat(4, 1fr)'                 // 4 columns
                            },
                        gap: '10px',
                        // Equal height rows (optional - can be auto)
                        gridAutoRows: 'min-content'
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
});

IndicatorGrid.displayName = 'IndicatorGrid';

export default IndicatorGrid;
